import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import {
  RESCHEDULE_APPROVER_LABELS,
  approverKeyForRole,
  canActOnRescheduleApproval,
  approvalStatusLabel,
  APPROVAL_STATUS_STYLES
} from '../constants/rescheduleApproval';

export default function InterviewRescheduleApprovals({ app, userRole, onUpdated }) {
  const qc = useQueryClient();
  const summary = app.rescheduleApprovalSummary;
  const sched = app.interviewSchedule;
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: payload =>
      api
        .post(`/applications/${app.id}/interview-reschedule-approval`, payload)
        .then(r => r.data),
    onSuccess: data => {
      setComment('');
      setError('');
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['myApplications'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      onUpdated?.(data);
      if (data?.rescheduleApprovalSummary?.allApproved) {
        setError('');
      }
    },
    onError: err => setError(err.response?.data?.message || 'Failed to record approval')
  });

  if (sched?.status !== 'reschedule_requested' || !summary) return null;

  const myKey = approverKeyForRole(userRole);
  const canAct = canActOnRescheduleApproval(userRole, summary);
  const myStatus = myKey ? summary.approvals[myKey]?.status : null;

  return (
    <div className="border border-amber-200 bg-amber-50/40 p-3 space-y-3">
      <div>
        <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Reschedule approvals</p>
        <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
          Admin, recruiter, and client must each approve before the new interview time is confirmed.
          Any rejection keeps the original schedule.
        </p>
      </div>

      {sched.rescheduleRequest?.proposedScheduledAt && (
        <p className="text-xs text-amber-900 bg-[hsl(var(--ds-surface))] border border-amber-100 px-2 py-1.5">
          <strong>Proposed:</strong>{' '}
          {new Date(sched.rescheduleRequest.proposedScheduledAt).toLocaleString()}
          {sched.rescheduleRequest.message ? ` — ${sched.rescheduleRequest.message}` : ''}
        </p>
      )}

      <ul className="space-y-1.5">
        {summary.required.map(key => {
          const entry = summary.approvals[key] || { status: 'pending' };
          const style =
            APPROVAL_STATUS_STYLES[entry.status] || APPROVAL_STATUS_STYLES.pending;
          return (
            <li
              key={key}
              className={`flex items-center justify-between gap-2 text-xs border px-2 py-1.5 ${style}`}
            >
              <span className="font-medium">{RESCHEDULE_APPROVER_LABELS[key]}</span>
              <span>{approvalStatusLabel(entry.status)}</span>
            </li>
          );
        })}
      </ul>

      {summary.allApproved && (
        <p className="text-xs text-green-800 bg-green-50 border border-green-200 px-2 py-1.5">
          All parties approved. Use &quot;Confirm candidate&apos;s proposed time&quot; below to apply the
          new schedule and notify the candidate.
        </p>
      )}

      {summary.rejected?.length > 0 && (
        <p className="text-xs text-red-700">
          Request declined ({summary.rejectedLabels.join(', ')}). Original interview time remains.
        </p>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      {canAct && (
        <div className="space-y-2 border-t border-amber-200/80 pt-2">
          <p className="text-xs font-medium text-[hsl(var(--ds-fg))]">
            Your decision ({RESCHEDULE_APPROVER_LABELS[myKey]})
          </p>
          <div className="field-with-label">
            <textarea
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              rows={2}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder=" "
            />
            <label>Comment (optional)</label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="border border-[hsl(var(--ds-success))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-success))] px-3 py-1.5 text-xs hover:bg-green-700 disabled:opacity-60"
              disabled={mutation.isLoading}
              onClick={() => mutation.mutate({ decision: 'approve', comment: comment.trim() })}
            >
              {mutation.isLoading ? 'Saving…' : 'Approve reschedule'}
            </button>
            <button
              type="button"
              className="border border-red-300 text-red-700 bg-[hsl(var(--ds-surface))] px-3 py-1.5 text-xs hover:bg-red-50 disabled:opacity-60"
              disabled={mutation.isLoading}
              onClick={() => mutation.mutate({ decision: 'reject', comment: comment.trim() })}
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {myKey && myStatus === 'approved' && !summary.allApproved && (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
          You approved. Waiting for: {summary.pendingLabels.join(', ')}.
        </p>
      )}
    </div>
  );
}
